"use client";

import { useRef, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/app/_module/components/ui/button";
import {
  managedMarketingApi,
  type Campaign,
  type CampaignDetail,
  type CampaignStatus,
} from "./managedMarketingApi";
import {
  dollarsToCents,
  errorMessage,
  money,
  safeWebUrl,
  statusLabels,
} from "./marketingHelpers";
import s from "./MarketingWorkspace.module.css";

const transitions: Record<CampaignStatus, CampaignStatus[]> = {
  requested: ["preparing", "rejected"],
  preparing: ["awaiting_approval", "rejected"],
  awaiting_approval: ["preparing", "rejected"],
  approved: ["live", "preparing", "paused", "completed"],
  live: ["paused", "completed"],
  pause_requested: ["paused", "completed"],
  paused: ["live", "preparing", "completed"],
  completed: [],
  rejected: [],
};

export default function CampaignOperations({
  campaign,
  ownerId,
  funding,
}: {
  campaign: Campaign;
  ownerId: string;
  funding: CampaignDetail["funding"];
}) {
  const client = useQueryClient();
  const [mode, setMode] = useState<"creative" | "status" | "spend" | "refund">(
    "creative",
  );
  const [notice, setNotice] = useState("");
  const refundRequest = useRef<{ key: string; id: string } | null>(null);
  const mutation = useMutation({
    mutationFn: async ({
      form,
      action,
    }: {
      form: FormData;
      action: typeof mode;
    }) => {
      const value = (key: string) => String(form.get(key) || "").trim();
      if (action === "creative") {
        const imageUrl = safeWebUrl(value("image"));
        if (!imageUrl)
          throw new Error(
            "Use a public HTTPS URL for the approved campaign image.",
          );
        return managedMarketingApi.update(campaign.id, {
          creative: {
            headline: value("headline"),
            body: value("body"),
            imageUrl,
            landingUrl: `${campaign.profileSnapshot.profileUrl}${campaign.profileSnapshot.profileUrl.includes("?") ? "&" : "?"}campaign=${campaign.id}`,
          },
          note: value("note"),
        });
      }
      if (action === "status") {
        const status = value("status") as CampaignStatus;
        const platform = value("platform");
        const campaignId = value("externalId");
        const impressions = value("impressions");
        const clicks = value("clicks");
        if (
          (impressions || clicks) &&
          (!value("reportedAt") || !value("source"))
        )
          throw new Error(
            "Include the report date and source with performance numbers.",
          );
        if ((platform && !campaignId) || (!platform && campaignId))
          throw new Error(
            "Enter both the advertising platform and campaign ID.",
          );
        return managedMarketingApi.update(campaign.id, {
          status,
          note: value("note"),
          ...(campaignId && {
            externalAdRefs: [
              {
                platform,
                campaignId,
                ...(value("adId") && { adId: value("adId") }),
              },
            ],
          }),
          ...((impressions || clicks) && {
            reporting: {
              ...(impressions && { impressions: Number(impressions) }),
              ...(clicks && { clicks: Number(clicks) }),
              reportedAt: new Date(value("reportedAt")).toISOString(),
              source: value("source"),
            },
          }),
        });
      }
      const amountCents = dollarsToCents(value("amount"));
      if (amountCents > campaign.balanceCents)
        throw new Error("This amount exceeds the campaign’s available funds.");
      if (action === "spend")
        return managedMarketingApi.spend(campaign.id, {
          amountCents,
          reference: value("reference"),
          occurredAt: new Date(value("occurredAt")).toISOString(),
          source: value("source"),
        });
      const key = `${value("payment")}:${amountCents}`;
      if (refundRequest.current?.key !== key)
        refundRequest.current = { key, id: crypto.randomUUID() };
      return managedMarketingApi.refund(campaign.id, {
        amountCents,
        paymentIntentId: value("payment"),
        requestId: refundRequest.current.id,
      });
    },
    onSuccess: async (_, variables) => {
      await client.invalidateQueries({
        queryKey: ["managed-marketing", ownerId],
      });
      setNotice(
        variables.action === "creative"
          ? "Creative saved. Send it for agent approval when it is ready."
          : variables.action === "spend"
            ? "Ad spend recorded."
            : variables.action === "refund"
              ? "Refund submitted to Stripe. The ledger updates when Stripe confirms it."
              : "Campaign updated.",
      );
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice("");
    mutation.mutate({ form: new FormData(event.currentTarget), action: mode });
  }
  const closed = ["rejected", "completed"].includes(campaign.status);
  const canEditCreative = ![
    "live",
    "pause_requested",
    "completed",
    "rejected",
  ].includes(campaign.status);
  const paid = funding.filter(
    (payment) => payment.paymentIntentId && payment.status === "paid",
  );
  return (
    <section
      className="mt-6 border-t pt-5"
      aria-label="Staff campaign controls"
    >
      {campaign.notes && (
        <div className="mb-5">
          <p className="mb-1 text-xs text-muted-foreground">Producer’s brief</p>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {campaign.notes}
          </p>
        </div>
      )}
      <div className={s.subtabs}>
        {(["creative", "status", "spend", "refund"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            aria-pressed={mode === tab}
            onClick={() => {
              setMode(tab);
              setNotice("");
              mutation.reset();
            }}
          >
            {tab === "creative"
              ? "Creative"
              : tab === "status"
                ? "Status & results"
                : tab === "spend"
                  ? "Record spend"
                  : "Refund"}
          </button>
        ))}
      </div>
      <form
        key={`${mode}-${campaign.updatedAt}`}
        onSubmit={submit}
        className="mt-5 space-y-4"
      >
        {mode === "creative" && (
          <>
            {!canEditCreative && (
              <p className={s.notice}>
                Pause an active campaign before replacing creative. Completed
                campaigns keep their approved record.
              </p>
            )}
            <label className={s.label}>
              Headline
              <input
                className={s.field}
                name="headline"
                maxLength={160}
                required
                defaultValue={campaign.creative?.headline || ""}
                disabled={!canEditCreative}
              />
            </label>
            <label className={s.label}>
              Ad copy
              <textarea
                className={s.field}
                name="body"
                rows={5}
                maxLength={4000}
                required
                defaultValue={campaign.creative?.body || ""}
                disabled={!canEditCreative}
              />
            </label>
            <label className={s.label}>
              Approved image URL
              <input
                className={s.field}
                name="image"
                type="url"
                required
                defaultValue={campaign.creative?.imageUrl || ""}
                placeholder="https://…"
                disabled={!canEditCreative}
              />
            </label>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Use Licensedproducer’s approved brand assets. Verify claims,
              license states, and the producer’s permission to use their photo.
              Saving a new version clears prior approval.
            </p>
            <label className={s.label}>
              Note for the producer
              <textarea
                className={s.field}
                name="note"
                rows={2}
                maxLength={2000}
                disabled={!canEditCreative}
              />
            </label>
            <Button
              type="submit"
              className={s.primary}
              disabled={mutation.isPending || !canEditCreative}
            >
              Save creative
            </Button>
          </>
        )}
        {mode === "status" && (
          <>
            <label className={s.label}>
              Campaign status
              <select
                className={s.field}
                name="status"
                defaultValue={campaign.status}
              >
                <option value={campaign.status}>
                  {statusLabels[campaign.status]}
                </option>
                {transitions[campaign.status].map((status) => (
                  <option key={status} value={status}>
                    {statusLabels[status]}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Mark live or paused only after verifying that state in the ad
              platform. A saved status does not publish or pause ads.
            </p>
            <div className={s.formGrid}>
              <label className={s.label}>
                Ad platform
                <input
                  className={s.field}
                  name="platform"
                  placeholder="Meta"
                  maxLength={100}
                  defaultValue={campaign.externalAdRefs[0]?.platform || ""}
                />
              </label>
              <label className={s.label}>
                Platform campaign ID
                <input
                  className={s.field}
                  name="externalId"
                  maxLength={200}
                  defaultValue={campaign.externalAdRefs[0]?.campaignId || ""}
                />
              </label>
            </div>
            <label className={s.label}>
              Ad ID (optional)
              <input
                className={s.field}
                name="adId"
                maxLength={200}
                defaultValue={campaign.externalAdRefs[0]?.adId || ""}
              />
            </label>
            <div className={s.formGrid}>
              <label className={s.label}>
                Total impressions
                <input
                  className={s.field}
                  name="impressions"
                  type="number"
                  min={0}
                  step={1}
                  defaultValue={campaign.reporting?.impressions}
                />
              </label>
              <label className={s.label}>
                Total clicks
                <input
                  className={s.field}
                  name="clicks"
                  type="number"
                  min={0}
                  step={1}
                  defaultValue={campaign.reporting?.clicks}
                />
              </label>
            </div>
            <label className={s.label}>
              Report date
              <input
                className={s.field}
                name="reportedAt"
                type="datetime-local"
                defaultValue={
                  campaign.reporting?.reportedAt
                    ? new Date(
                        new Date(campaign.reporting.reportedAt).getTime() -
                          new Date(
                            campaign.reporting.reportedAt,
                          ).getTimezoneOffset() *
                            60000,
                      )
                        .toISOString()
                        .slice(0, 16)
                    : ""
                }
              />
            </label>
            <label className={s.label}>
              Report source
              <input
                className={s.field}
                name="source"
                maxLength={200}
                placeholder="Meta Ads Manager export"
                defaultValue={campaign.reporting?.source || ""}
              />
            </label>
            <label className={s.label}>
              Update for the producer
              <textarea
                className={s.field}
                name="note"
                rows={2}
                maxLength={2000}
              />
            </label>
            <Button
              type="submit"
              className={s.primary}
              disabled={mutation.isPending}
            >
              Save update
            </Button>
          </>
        )}
        {mode === "spend" && (
          <>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Record a new, verified spend amount. Use a unique platform report
              reference for each entry. Available funds:{" "}
              {money(campaign.balanceCents)}.
            </p>
            <label className={s.label}>
              Additional spend (USD)
              <input
                className={s.field}
                name="amount"
                inputMode="decimal"
                required
              />
            </label>
            <label className={s.label}>
              Report reference
              <input
                className={s.field}
                name="reference"
                maxLength={200}
                required
                placeholder="Platform report or settlement ID"
              />
            </label>
            <label className={s.label}>
              When was it spent?
              <input
                className={s.field}
                name="occurredAt"
                type="datetime-local"
                required
              />
            </label>
            <label className={s.label}>
              Source
              <input
                className={s.field}
                name="source"
                maxLength={200}
                required
                placeholder="Meta Ads Manager"
              />
            </label>
            <Button
              type="submit"
              className={s.primary}
              disabled={mutation.isPending || campaign.balanceCents <= 0}
            >
              Record spend
            </Button>
          </>
        )}
        {mode === "refund" && (
          <>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Return unused campaign funds to the original payment. Confirm the
              campaign is paused or completed and all platform spend is recorded
              first.
            </p>
            <label className={s.label}>
              Original payment
              <select className={s.field} name="payment" required>
                <option value="">Select a confirmed payment</option>
                {paid.map((payment) => (
                  <option key={payment.id} value={payment.paymentIntentId!}>
                    {money(payment.amountCents)} ·{" "}
                    {new Date(payment.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </label>
            <label className={s.label}>
              Refund amount (USD)
              <input
                className={s.field}
                name="amount"
                inputMode="decimal"
                required
              />
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" required className="mt-1" />I have verified
              the final spend and refund amount.
            </label>
            <Button
              type="submit"
              variant="outline"
              disabled={
                mutation.isPending ||
                !paid.length ||
                campaign.balanceCents <= 0 ||
                (!closed && campaign.status !== "paused")
              }
            >
              Refund original payment
            </Button>
          </>
        )}
        {mutation.isPending && (
          <p role="status" className="text-xs text-muted-foreground">
            Saving…
          </p>
        )}
        {mutation.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(mutation.error)}
          </p>
        )}
        {notice && (
          <p role="status" className="text-sm">
            {notice}
          </p>
        )}
      </form>
    </section>
  );
}
